/* eslint-disable @next/next/no-img-element */
'use client';

import useCheckBreakpointActive from '@/hooks/useBreakpoint';
import getQualifiedImageUrl from '@/utils/imageUrlProcessor';
import Button from '@ui/Button';
import { cx } from 'cva';
import { isString, set } from 'lodash-es';
import Image from 'next/image';
import React from 'react';
import {
  MdClose,
  MdOutlineChevronLeft,
  MdOutlineChevronRight,
} from 'react-icons/md';
import { useSpringCarousel } from 'react-spring-carousel';
import VideoPlayer from '../VideoPlayer';

const getGridColsForItems = (numItems) => {
  return `grid-cols-${numItems}`;
};

function ImageCarousel(props) {
  const {
    images,
    type,
    numItemsToShow = 4,
    onChange,
    disabled = false,
  } = props;
  const isSmallScreen = useCheckBreakpointActive('sm');
  const itemsPerSlide = isSmallScreen ? 1 : numItemsToShow;

  const carouselItems = images.map((image, index) => {
    if (type === 'video') {
      return {
        id: index,
        url: image.url,
      };
    } else {
      if (isString(image)) {
        return {
          id: index,
          preview: getQualifiedImageUrl(image),
        };
      }

      return {
        id: index,
        preview: image.preview,
      };
    }
  });

  if (carouselItems.length < itemsPerSlide) {
    const emptyItems = Array(itemsPerSlide - carouselItems.length)
      .fill()
      .map((i, index) => ({ id: `empty-${index}`, empty: true }));
    carouselItems.push(...emptyItems);
  }

  const handleRemoveItem = (index) => {
    set(carouselItems, `${index}.deleted`, true);
    const filtered = images.filter((_, i) => i !== index);
    onChange(filtered);
  };

  const renderItem = (item, index) => {
    if (item.empty) {
      return <div key={item.id} className="h-full w-full" />;
    }

    return (
      <div className="flex w-full flex-col items-start gap-2">
        <div
          className={cx(
            'relative h-[118px] w-full',
            item.deleted && 'opacity-50',
          )}>
          <>
            {type === 'video' ? (
              <VideoPlayer
                src={item.url}
                className={cx('rounded', 'object-cover')}>
                Video load failed
              </VideoPlayer>
            ) : (
              <Image
                src={item.preview || ''}
                alt="post image"
                fill
                className="rounded object-cover"
              />
            )}
          </>
          {!item.deleted && !disabled && (
            <button
              className="p-0.25 -translate-x-0.25 translate-y-0.25 absolute right-0 top-0 transform rounded-full bg-white"
              onClick={() => handleRemoveItem(index)}>
              <MdClose className="h-4 w-4 text-secondary" />
            </button>
          )}
        </div>
      </div>
    );
  };

  const canNavigate = images.length > itemsPerSlide;

  const { carouselFragment, slideToPrevItem, slideToNextItem } =
    useSpringCarousel({
      slideAmount: 200,
      withLoop: canNavigate,
      itemsPerSlide: itemsPerSlide,
      gutter: isSmallScreen ? 0 : 24,
      items: carouselItems.map((item, index) => ({
        id: item.id,
        renderItem: renderItem(item, index),
      })),
    });

  const handleNextClick = () => {
    slideToNextItem();
  };

  const handlePrevClick = () => {
    slideToPrevItem();
  };

  return (
    <div className="relative flex w-full items-center justify-center">
      <div className="flex-1 overflow-hidden">{carouselFragment}</div>

      {canNavigate && (
        <>
          <Button
            variant="icon"
            size="sm"
            onClick={handlePrevClick}
            className="absolute left-0 top-1/3 bg-body-background "
            disabled={!canNavigate}>
            <MdOutlineChevronLeft
              className={`h-6 w-6 ${
                canNavigate ? 'text-text-disabled' : 'hidden'
              }`}
            />
          </Button>

          <Button
            variant="icon"
            size="sm"
            onClick={handleNextClick}
            className="absolute right-0 top-1/3 bg-body-background "
            disabled={!canNavigate}>
            <MdOutlineChevronRight
              className={`h-6 w-6 ${
                canNavigate ? 'text-text-disabled' : 'hidden'
              }`}
            />
          </Button>
        </>
      )}
    </div>
  );
}

export default ImageCarousel;
